"use client"

import { BffApiClientMolecule } from "@/app/api-client/store"
import { useInfiniteTableItems } from "@/app/components/infinite-table/use-infinite-table-items"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { UnionAddress } from "@anaconda/core/blockchain/address"
import { Trans } from "@lingui/react/macro"
import { computed } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useSuspenseInfiniteQuery } from "@tanstack/react-query"
import { molecule, useMolecule } from "bunshi/react"
import { useMemo } from "react"
import { CollectionMoleculeReatom as CollectionMolecule } from "../store/index.reatom"
import { BidsTableReatom } from "./bids-table.reatom"
import { getCollectionMyOffersQueryParams } from "./utils"

export const CollectionMyOffersMoleculeReatom = molecule((mol) => {
  const { collectionId } = mol(CollectionMolecule)
  const bffApiClient = mol(BffApiClientMolecule)

  const buildQueryParams = (maker: UnionAddress) =>
    getCollectionMyOffersQueryParams(bffApiClient, collectionId, maker)

  return { buildQueryParams }
})

/**
 * Split in two so the suspense query only ever runs with a real maker —
 * useSuspenseInfiniteQuery cannot be disabled.
 */
export const CollectionMyOffersReatom = reatomComponent(() => {
  const { unionAddress } = useMolecule(WalletConnectMoleculeReatom)
  const maker = unionAddress()

  if (!maker) {
    return (
      <div className="py-10 text-center text-foreground/60">
        <Trans>Connect your wallet to see your offers</Trans>
      </div>
    )
  }

  return <MyOffersTable maker={maker} />
}, "CollectionMyOffers")

const MyOffersTable = reatomComponent<{ maker: UnionAddress }>(({ maker }) => {
  const { buildQueryParams } = useMolecule(CollectionMyOffersMoleculeReatom)
  const queryParams = useMemo(
    () => computed(() => buildQueryParams(maker), "collectionMyOffersParams"),
    [buildQueryParams, maker],
  )
  const query = useSuspenseInfiniteQuery(queryParams())

  const queryResult = useMemo(
    () => ({
      ...query,
      pages: query.data.pages.map((page) => ({ items: page.bids })),
    }),
    [query],
  )

  const { items, loadNext } = useInfiniteTableItems(queryResult)

  return (
    <BidsTableReatom
      items={items}
      loadNext={loadNext}
      emptyState={<Trans>You have no offers in this collection</Trans>}
    />
  )
}, "MyOffersTable")
